/*!
 * Copyright © 2019-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import '@angular/localize/init';
import { IVehicleTachoStatus } from '../../models';

@Component({
  selector: 'lib-tacho-balloon-driving-info',
  templateUrl: './tacho-balloon-driving-info.component.html',
  styleUrls: ['./tacho-balloon-driving-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TachoBalloonDrivingInfoComponent implements OnChanges {
  @Input()
  public tacho: IVehicleTachoStatus;
  public initialTacho: IVehicleTachoStatus;
  public hasInitialized = false;
  public driveTimeRemainingToday_tooltip: string;
  public driveTimeRemainingWeek_tooltip: string;
  public onDutyRemainingToday_tooltip: string;
  public onDutyRemainingWeek_tooltip: string;
  public nextBreakDue_tooltip: string;
  public extendedDrivesRemaining_tooltip: string;
  public reducedRestRemaining_tooltip: string;
  public hasNextBreakDue = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tacho'] && !this.hasInitialized) {
      this.initialTacho = { ...changes['tacho'].currentValue };
      this.hasInitialized = true;
    }

    if (this._hasTachoData()) {
      this.hasNextBreakDue = this.tachoHasNextBreakDue();
      this.driveTimeRemainingToday_tooltip = this.getDriveTimeRemainingTodayTooltip();
      this.driveTimeRemainingWeek_tooltip = this.getDriveTimeRemainingWeekTooltip();
      this.onDutyRemainingToday_tooltip = this.getOnDutyRemainingTodayTooltip();
      this.onDutyRemainingWeek_tooltip = this.getOnDutyRemainingWeekTooltip();
      this.nextBreakDue_tooltip = this.getNextBreakDueTooltip();
      this.extendedDrivesRemaining_tooltip = this.getExtendedDrivesRemainingTooltip();
      this.reducedRestRemaining_tooltip = this.getReducedRestRemainingTooltip();
    }
  }

  private _hasTachoData(): boolean {
    return this.initialTacho != null && this.initialTacho.DriverWorkingTime != null;
  }

  public getHoursStringFromSeconds(seconds: number): string {
    const hours = Math.abs(Math.trunc(seconds / 3600));
    if (seconds < 0) {
      return '-' + hours.toString();
    }
    return hours.toString();
  }

  public getMinutesStringFromSeconds(seconds: number): string {
    const minutes = Math.abs(Math.trunc((seconds / 60) % 60));

    if (minutes < 9) {
      return '0' + minutes;
    } else {
      return minutes.toString();
    }
  }

  public tachoHasNextBreakDue(): boolean {
    return this.initialTacho.DriverWorkingTime.NextBreakDue != null;
  }

  getDriveTimeRemainingTodayTooltip() {
    if (this.initialTacho.DriverWorkingTime.ExtendedDrivesRemaining > 0) {
      return $localize`:@@S_TACHO_DP_DRIVE_REMAINING_D_10H_VIOLATION_TOOLTIP:This driver has driven longer than the 10 hours allowed for the day.`;
    } else {
      return $localize`:@@S_TACHO_DP_DRIVE_REMAINING_D_9H_VIOLATION_TOOLTIP:This driver has driven longer than the 9 hours allowed for the day. They have no more extensions left.`;
    }
  }

  getDriveTimeRemainingWeekTooltip() {
    return $localize`:@@S_TACHO_DP_DRIVE_REMAINING_W_VIOLATION_TOOLTIP:This driver has driven longer than the 56 hours allowed for the week.`;
  }

  getOnDutyRemainingTodayTooltip() {
    if (this.initialTacho.DriverWorkingTime.ReducedRestRemaining > 0) {
      return $localize`:@@S_TACHO_DP_ON_REMAINING_D_EXT_VIOLATION_TOOLTIP:This driver has been on duty longer than the 15 hours allowed for the day.`;
    } else {
      return $localize`:@@S_TACHO_DP_ON_REMAINING_D_VIOLATION_TOOLTIP:This driver has been on duty longer than the 13 hours allowed for the day. They have no more reductions left.`;
    }
  }

  getOnDutyRemainingWeekTooltip() {
    return $localize`:@@S_TACHO_DP_ON_REMAINING_W_VIOLATION_TOOLTIP:This driver has been on duty longer than the 60 hours allowed for the week.`;
  }

  getNextBreakDueTooltip() {
    return $localize`:@@S_TACHO_DP_NEXT_BREAK_DUE_VIOLATION_TOOLTIP:This driver has gone over the time allowed without a break.`;
  }

  getExtendedDrivesRemainingTooltip() {
    return $localize`:@@S_TACHO_DP_EXTENDED_DRIVING_VIOLATION_TOOLTIP:This driver has extended their driving more than the two times allowed.`;
  }

  getReducedRestRemainingTooltip() {
    return $localize`:@@S_TACHO_DP_REDUCED_REST_VIOLATION_TOOLTIP:This driver has used up all their reduced rest periods for this week and must take a full rest period.`;
  }

  hasAllNeededDrivingInfo(): boolean {
    return !!(
      this.initialTacho?.DriverWorkingTime &&
      this.initialTacho.DriverWorkingTime.DriveTimeRemainingToday != null &&
      this.initialTacho.DriverWorkingTime.DriveTimeRemainingWeek != null &&
      this.initialTacho.DriverWorkingTime.OnDutyRemainingToday != null &&
      this.initialTacho.DriverWorkingTime.OnDutyRemainingWeek != null &&
      this.initialTacho.DriverWorkingTime.ExtendedDrivesRemaining != null &&
      this.initialTacho.DriverWorkingTime.ReducedRestRemaining != null
    );
  }
}
